import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {Evaluator, Version} from "@cm/graph-v3"
import {versionChain} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import type {NumberLike, ObjectLikeNode, PositionValue, ToneMapping} from "#template/nodes/types"
import {positionValue, toneMapping} from "#template/nodes/validation"
import {numberLike} from "#template/nodes/number/fundamental"
import {objectLike} from "#template/nodes/object/fundamental"
import {TargetableObjectNode} from "#template/nodes/object/targetable-object-node"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import type {OnCompileContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {objectNodeParameters} from "#template/nodes/object/validation"
import {defaultsForToneMapping} from "#template/nodes/render/tone-mapping"
import {Matrix4, Vector3} from "@cm/math"
import type {SceneNodes} from "#template/interfaces/scene-object"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import {cameraAutomaticTarget, automaticVerticalTilt as makeAutomaticVerticalTiltMatrix} from "#template/utils/camera-utils"
import {transformBounds} from "#template/utils/scene-geometry-utils"
import {visitNone} from "#template/template-node/visit-helpers"
import {RaycastFocalDistance} from "#template/runtime-graph/nodes/raycast-focal-distance"

export type CameraParameters = {
    name: string
    resolutionX: NumberLike
    resolutionY: NumberLike
    target: PositionValue
    targeted: boolean
    filmGauge: number
    fStop: number
    focalLength: number
    autoFocus: boolean
    focalDistance: number
    zoomFactor?: number
    ev: number
    shiftX: number
    shiftY: number
    automaticVerticalTilt: boolean
    enableOrbiting: boolean
    minDistance?: NumberLike
    maxDistance?: NumberLike
    minPolarAngle?: NumberLike
    maxPolarAngle?: NumberLike
    minAzimuthAngle?: NumberLike
    maxAzimuthAngle?: NumberLike
    enablePanning: boolean
    screenSpacePanning: boolean
    adaptAspectRatio: boolean
    toneMapping: ToneMapping
    automaticTarget?: ObjectLikeNode
    nearClip?: number
    farClip?: number
    enableDamping: boolean
    dampingFactor: number
} & ObjectNodeParameters

export type CameraConstructorParameters = Omit<CameraParameters, "enableDamping" | "dampingFactor" | "enableOrbiting" | "adaptAspectRatio" | "visible"> & {
    enableDamping?: boolean
    dampingFactor?: number
    enableOrbiting?: boolean
    adaptAspectRatio?: boolean
    visible?: boolean
}

type V0 = {
    name: string
    resolutionX: NumberLike
    resolutionY: NumberLike
    target: PositionValue
    filmGauge: number
    fStop: number
    focalLength: number
    autoFocus: boolean
    focalDistance: number
    zoomFactor?: number
    exposure: number
    shiftX: number
    shiftY: number
    automaticVerticalTilt: boolean
    minDistance?: number
    maxDistance?: number
    minPolarAngle?: number
    maxPolarAngle?: number
    minAzimuthAngle?: number
    maxAzimuthAngle?: number
    enablePanning: boolean
    screenSpacePanning: boolean
    toneMapping?: ToneMapping
    automaticTarget?: ObjectLikeNode
    nearClip?: number
    farClip?: number
} & ObjectNodeParameters

type V1 = V0 & {toneMapping: ToneMapping; targeted: boolean}
type V2 = Omit<V1, "exposure"> & {ev: number}
type V3 = V2 & {enableDamping: boolean; dampingFactor: number}

const v0: Version<V0, V1> = {
    toNextVersion: (parameters) => {
        return {...parameters, targeted: true, toneMapping: parameters.toneMapping ?? defaultsForToneMapping("filmic")}
    },
}

const v1: Version<V1, V2> = {
    toNextVersion: (parameters) => {
        const {exposure, ...rest} = parameters
        return {ev: Math.log2(exposure), ...rest}
    },
}

const v2: Version<V2, V3> = {
    toNextVersion: (parameters) => {
        const legacyGlobalRenderConstantsExposureScale = 0.8
        const {ev, ...rest} = parameters
        const exposure = Math.pow(2, ev) * legacyGlobalRenderConstantsExposureScale
        return {ev: Math.log2(exposure), enableDamping: false, dampingFactor: 0.05, ...rest}
    },
}

const cameraDefinition: ObjectNodeDefinition<CameraParameters, CameraConstructorParameters> = {
    nodeClass: "Camera",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                resolutionX: numberLike,
                resolutionY: numberLike,
                target: positionValue,
                targeted: z.boolean(),
                filmGauge: z.number(),
                fStop: z.number(),
                focalLength: z.number(),
                autoFocus: z.boolean(),
                focalDistance: z.number(),
                zoomFactor: z.optional(z.number()),
                ev: z.number(),
                shiftX: z.number(),
                shiftY: z.number(),
                automaticVerticalTilt: z.boolean(),
                enableOrbiting: z._default(z.boolean(), true),
                minDistance: z.optional(numberLike),
                maxDistance: z.optional(numberLike),
                minPolarAngle: z.optional(numberLike),
                maxPolarAngle: z.optional(numberLike),
                minAzimuthAngle: z.optional(numberLike),
                maxAzimuthAngle: z.optional(numberLike),
                enablePanning: z.boolean(),
                screenSpacePanning: z.boolean(),
                adaptAspectRatio: z._default(z.boolean(), true),
                toneMapping: toneMapping,
                automaticTarget: z.optional(objectLike),
                nearClip: z.optional(z.number()),
                farClip: z.optional(z.number()),
                enableDamping: z._default(z.boolean(), false),
                dampingFactor: z._default(z.number(), 0.05),
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([v0, v1, v2]),
}

@registerTemplateGraphNode
export class Camera extends TargetableObjectNode<CameraParameters, CameraConstructorParameters> {
    public override get definition() {
        return cameraDefinition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<CameraParameters> {
        const {evaluator, templateDepth} = context
        const {templateContext} = evaluator
        const {sceneManager, logger} = templateContext
        const {
            targeted,
            zoomFactor,
            filmGauge,
            automaticVerticalTilt,
            fStop,
            ev,
            shiftX,
            nearClip,
            farClip,
            enableOrbiting,
            minDistance,
            maxDistance,
            minPolarAngle,
            maxPolarAngle,
            minAzimuthAngle,
            maxAzimuthAngle,
            enablePanning,
            screenSpacePanning,
            adaptAspectRatio,
            toneMapping,
            enableDamping,
            dampingFactor,
        } = this.parameters

        const scope = evaluator.getScope(this)

        this.setupObject(scope, context, "Camera", undefined, undefined, ({transform: objectTransform, isInteracting, ...objectProps}) => {
            const viewportSizeParameter = ((): [number, number] | undefined => {
                const rootParameters = sceneManager.getRootNode().parameters.parameters
                const rootViewportSizeParameter = rootParameters?.parameters["$viewportSize"]
                if (Array.isArray(rootViewportSizeParameter) && rootViewportSizeParameter.length === 2) {
                    const [x, y] = rootViewportSizeParameter
                    if (typeof x === "number" && typeof y === "number") return [x, y] as const
                }
                return undefined
            })()

            const nullToZero = (number: BuilderInlet<number | null>, uniqueId: string) => scope.pureLambda(number, (number) => number ?? 0, uniqueId)

            const resolutionX = nullToZero(evaluator.evaluate(this.parameters.resolutionX), "resolutionX")
            const resolutionY = nullToZero(evaluator.evaluate(this.parameters.resolutionY), "resolutionY")

            const uiSize = scope.pureLambda(
                // pure-lambda-allow: logger
                scope.tuple(viewportSizeParameter, resolutionX, resolutionY),
                ([viewportSizeParameter, resolutionX, resolutionY]): [number, number] => {
                    const returnValue = viewportSizeParameter ?? [resolutionX, resolutionY]
                    if (returnValue[0] <= 0 || returnValue[1] <= 0) {
                        logger.warn(`Invalid camera viewport size: ${returnValue}`)
                        return [1000, 1000]
                    }
                    return returnValue
                },
                "uiSize",
            )

            const focalLength = scope.pureLambda(
                scope.tuple(filmGauge, this.parameters.focalLength, zoomFactor, uiSize),
                ([filmGauge, focalLength, zoomFactor, uiSize]) => {
                    if (zoomFactor !== undefined) {
                        return adjustFocalLengthForZoomFactor(filmGauge, focalLength, zoomFactor, uiSize[0], uiSize[1])
                    }
                    return focalLength
                },
                "focalLength",
            )

            const [automaticTarget, automaticTargetUndefined] = scope.branch(evaluator.evaluate(this.parameters.automaticTarget ?? null))
            const autoTargetBounds = scope.phi(scope.get(automaticTarget, "bounds"), automaticTargetUndefined)

            const constrainedTransform = this.targetedTransform(scope, objectTransform, isInteracting)
            const resolvedTarget = this.setupTargetLiveValue(scope, context)

            const transformAndFocus = scope.pureLambda(
                scope.tuple(constrainedTransform, autoTargetBounds, focalLength, filmGauge, uiSize, resolvedTarget, this.parameters.focalDistance),
                ([transform, autoTargetBounds, focalLength, filmGauge, uiSize, target, focalDistance]) => {
                    if (autoTargetBounds) {
                        const position = transform.getTranslation()
                        const transformedBounds = transformBounds(autoTargetBounds, Matrix4.fromArray(autoTargetBounds.matrix))
                        const target = Vector3.fromArray(transformedBounds.centroid)
                        const matrix = cameraAutomaticTarget(position, target, transformedBounds.aabb, focalLength, filmGauge, uiSize)
                        return [matrix, target, matrix.getTranslation().sub(target).norm()] as const
                    } else return [transform, target, focalDistance] as const
                },
                "autoTargetBounds",
            )

            const autoTargetTransform = scope.get(transformAndFocus, 0)
            const target = scope.get(transformAndFocus, 1)
            const fallbackFocalDistance = scope.get(transformAndFocus, 2)

            const transformAndShift = (() => {
                if (!automaticVerticalTilt) return scope.tuple(autoTargetTransform, this.parameters.shiftY)
                else {
                    return scope.pureLambda(
                        scope.tuple(autoTargetTransform, filmGauge, focalLength, uiSize, this.parameters.shiftY),
                        ([autoTargetTransform, filmGauge, focalLength, uiSize, shiftY]) => {
                            const [transform, additionalShiftY] = makeAutomaticVerticalTiltMatrix(
                                autoTargetTransform,
                                filmGauge,
                                focalLength,
                                uiSize[0],
                                uiSize[1],
                            )
                            return [transform, shiftY + additionalShiftY] as const
                        },
                        "matrixAndShiftY",
                    )
                }
            })()

            const transform = scope.get(transformAndShift, 0)
            const shiftY = scope.get(transformAndShift, 1)

            const autoFocus = scope.phi(
                scope.pureLambda(automaticTarget, () => false, "autoFocusAutoTarget"),
                scope.pureLambda(
                    scope.tuple(automaticTargetUndefined, this.parameters.autoFocus),
                    ([, autoFocus]) => autoFocus,
                    "autoFocusUndefinedAutoTarget",
                ),
            )

            const {focalDistance} = scope.node(RaycastFocalDistance, {
                meshGeometryQueries: sceneManager.getMeshGeometryQueries(),
                transform,
                autoFocus,
                fallbackFocalDistance,
            })

            const nullToUndefined = (number: BuilderInlet<number | undefined | null>, uniqueId: string) =>
                scope.pureLambda(number, (number) => number ?? undefined, uniqueId)

            return scope.struct<SceneNodes.Camera>("Camera", {
                type: "Camera",
                templateDepth,
                ...objectProps,
                transform,
                focalLength,
                focalDistance,
                filmGauge,
                fStop,
                exposure: Math.pow(2, ev),
                shiftX,
                shiftY,
                zoomFactor: zoomFactor ?? 1,
                nearClip,
                farClip,
                autoFocus,
                resolutionX,
                resolutionY,
                target,
                targeted: scope.pureLambda(
                    scope.tuple(autoTargetBounds, targeted),
                    ([autoTargetBounds, targeted]) => {
                        if (autoTargetBounds !== null) return true
                        return targeted
                    },
                    "targeted",
                ),
                realtimeSettings: scope.struct("CameraRealtimeSettings", {
                    toneMapping,
                    enableOrbiting,
                    minDistance: nullToUndefined(evaluator.evaluate(minDistance), "minDistance"),
                    maxDistance: nullToUndefined(evaluator.evaluate(maxDistance), "maxDistance"),
                    minPolarAngle: nullToUndefined(evaluator.evaluate(minPolarAngle), "minPolarAngle"),
                    maxPolarAngle: nullToUndefined(evaluator.evaluate(maxPolarAngle), "maxPolarAngle"),
                    minAzimuthAngle: nullToUndefined(evaluator.evaluate(minAzimuthAngle), "minAzimuthAngle"),
                    maxAzimuthAngle: nullToUndefined(evaluator.evaluate(maxAzimuthAngle), "maxAzimuthAngle"),
                    enablePanning,
                    screenSpacePanning,
                    adaptAspectRatio,
                    enableDamping,
                    dampingFactor,
                }),
            })
        })

        return visitNone(this.parameters)
    }
}

function adjustFocalLengthForZoomFactor(filmGauge: number, focalLength: number, zoomFactor: number, width: number, height: number): number {
    const maxFOV = 175.0 * (Math.PI / 180.0)
    const aspect = width > height ? width / height : height / width
    const origFOV = 2 * Math.atan(filmGauge / aspect / (2 * focalLength))
    const newFOV = Math.min(origFOV / Math.max(0.01, zoomFactor), maxFOV)
    return filmGauge / aspect / (2 * Math.tan(newFOV / 2))
}
